import type { CallContext } from "./context";
import { ApiError, NoSuccessError } from "./error";

type ServerResponse = SuccessServerResponse | ErrorServerResponse;

interface SuccessServerResponse {
  success: true;
  response: unknown;
}

interface ErrorServerResponse {
  success: false;
  error: { message: string; code?: string; details?: unknown };
}

function hasResponseFormat(response: unknown): response is ServerResponse {
  return isUnknownRecord(response) && "success" in response;
}

function isSuccessResponse(response: unknown): response is SuccessServerResponse {
  return hasResponseFormat(response) && response.success && "response" in response;
}

function isErrorResponse(response: unknown): response is ErrorServerResponse {
  return (
    hasResponseFormat(response) &&
    !response.success &&
    "error" in response &&
    isUnknownRecord(response.error) &&
    "message" in response.error
  );
}

export function handleResponse(response: unknown, context: CallContext) {
  if (isSuccessResponse(response)) {
    return response.response;
  }
  if (isErrorResponse(response)) {
    throw new NoSuccessError(response.error.message, context.action, {
      request: context,
      response: response.error,
    });
  }
  throw new ApiError(
    "Bad Response Format",
    `Unexpected server response format calling RPC method ${context.action}.`,
    { request: context, response: isUnknownRecord(response) ? response : { value: response } },
  );
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
